// @flow strict
import * as React from 'react';
// $FlowFixMe[untyped-import]
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useState, useMemo} from 'react';
import {useDispatch} from 'react-redux';
// $FlowFixMe[nonstrict-import] - need to upgrade this
import {captureApiError, ApiError} from 'src/utils/errors';
// $FlowFixMe[nonstrict-import] - need to upgrade this
import {camel} from 'src/utils/camel-snake';
// $FlowFixMe[untyped-import]
import {serializeQuery} from 'src/utils';
// $FlowFixMe[nonstrict-import]
import {defaultState, type ResourceState} from './useResource';
// $FlowFixMe[nonstrict-import]
import {baseRequest} from 'src/utils/redux-api-v2';


export const IGNORED_STATUS_CODES = [401, 404];

export type {ResourceState};
type Query = {[string]: string, ...};

export type BaseApiOptions = {
  method?: 'GET',
  onFetch?: () => mixed,
  handleError?: (ApiError) => mixed,
  shouldFetch?: boolean,
  shouldCamel?: boolean,
  cacheTimeout?: number,
  apiPath?: string,
  deps?: mixed[],
  key?: string | Array<string>,
  // all 400 <= status < 500 codes get logged _to sentry_
  // unless they're included here. We don't log 401 or 404s.
  // only update this to maintain a semantic api contract
  // but to avoid spamming sentry.
  ignoredStatusCodes?: Array<number>,
};
export type ApiUrl = string | {|pathname: string, query?: Query|} | null | void;
export type ApiUrlArg = ApiUrl | (() => ApiUrl);

type Refetch = (force?: boolean) => void;

// Helper to normalize URL
const normalizeUrl = (url: ApiUrl): {|pathname: ?string, query: ?Query|} => {
  if (!url) {
    return {pathname: null, query: null};
  }
  if (typeof url === 'string') {
    return {pathname: url, query: null};
  }
  return {
    pathname: url.pathname,
    query: url.query || null,
  };
};

type ExtendedApiOptions<Response> = {|
  ...BaseApiOptions,
  handleResponse?: (Response) => mixed,
|};

/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */
export function useAdvancedApi<Response>(
  url: ApiUrl,
  {
    method = 'GET',
    key,
    onFetch,
    handleResponse,
    handleError,
    shouldFetch = true,
    shouldCamel,
    cacheTimeout,
    ignoredStatusCodes = IGNORED_STATUS_CODES,
    deps,
    ...options
  }: ExtendedApiOptions<Response>,
): Refetch {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const {pathname, query} = normalizeUrl(url);

  //NOTE:(diwakersurya) if key is an array, it will be used as the query key

  const queryKey = key
    ? Array.isArray(key)
      ? key
      : [key]
    : [pathname, query, method];

  const queryFn = async ({
    signal,
  }: {|
    signal?: AbortSignal,
  |}): Promise<?Response> => {
    if (!pathname || !shouldFetch) {
      return null;
    }

    try {
      onFetch?.();
      const response = await dispatch(
        baseRequest(pathname, {
          method,
          query: query || undefined,
          signal,
          options: {
            ...options,
            handleAuth: true,
          },
        }),
      );
      const result = shouldCamel ? camel(response) : response;
      handleResponse?.(result);
      return result;
    } catch (error) {
      if (error instanceof ApiError) {
        const {status} = error.response;
        if (status < 500 && !ignoredStatusCodes.includes(status)) {
          captureApiError(error);
        }
        handleError?.(error);
      }
      throw error;
    }
  };

  const {refetch} = useQuery({
    queryKey,
    queryFn,
    enabled: Boolean(pathname && shouldFetch),
    staleTime: cacheTimeout,
    cacheTime: cacheTimeout,
  });

  return (force?: boolean) => {
    if (force) {
      queryClient.removeQueries(queryKey);
    }
    refetch();
  };
}

export type ApiState<Response> = {|
  ...ResourceState<Response>,
  refetch: () => void,
|};

/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */
export function useApi<Response>(
  url: ApiUrl,
  optionsArg?: ExtendedApiOptions<Response> | Array<mixed>,
): ApiState<Response> {
  const [state, setState] = useState<ResourceState<Response>>({
    ...defaultState,
    isLoading: Boolean(url),
  });

  const options = {
    onFetch: () => {
      setState((state) => ({...state, isLoading: true}));
    },
    handleResponse: (result: Response) => {
      setState({
        result,
        error: null,
        isLoading: false,
      });
    },
    handleError: (error: Error) => {
      setState({
        result: null,
        error,
        isLoading: false,
      });
    },
  };

  const refetch = useAdvancedApi<Response>(
    url,
    Array.isArray(optionsArg)
      ? {
          ...options,
          deps: optionsArg,
        }
      : {
          ...optionsArg,
          ...options,
        },
  );

  return {
    ...state,
    refetch,
  };
}

export function useApiStub<Stub>(stub: Stub): ApiState<Stub> {
  return {
    ...defaultState,
    isLoading: false,
    result: stub,
    refetch: () => {
      // pass
    },
  };
}

/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */
export function useLegacyApi<Response>(
  url: ApiUrl,
  options?: ExtendedApiOptions<Response>,
): ?Response {
  const [response, setResponse] = useState<?Response>(null);

  useAdvancedApi<Response>(url, {
    ...options,
    handleResponse: (response) => {
      setResponse(response);
    },
  });

  return response;
}
/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */

export function useLegacyApiWithParams<Response>(
  getUrl: () => ApiUrl,
  params: Array<mixed>,
  options?: ExtendedApiOptions<Response>,
): ?Response {
  const url = useMemo(getUrl, params);
  return useLegacyApi(url, options);
}
/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */
export function useLoadingApi<Response>(
  getUrl: () => ApiUrl,
  params: Array<mixed>,
  options?: ExtendedApiOptions<Response>,
): {response: ?Response, loaded: boolean} {
  const response = useLegacyApiWithParams(getUrl, params, options);
  return {response, loaded: response != null};
}

/**
 * Hook that combines useAdvancedApi functionality with React Query options
 * @template Response The expected response type
 * @param {ApiUrl} url The API URL to fetch from
 * @param {Object} options Combined options for API and React Query
 * @param {Object} options.queryOptions React Query specific options (like enabled, staleTime etc)
 * @param {Object} options.apiOptions Options for the API call (like method, query etc)
 * @returns {Object} React Query result object with data, error, isLoading etc
 */

export type ApiOptions = {
  handleAuth?: boolean,
  // TODO (kyle): this property is too multi-purpose
  // Don't print stuff to console, modals, etc.
  silence?: boolean,
  redirect?: 'follow' | 'manual' | 'error',
  apiPath?: string,
  headers?: {
    [string]: string,
  },
};
export type BaseApiRequestOptions = {
  signal?: AbortSignal,
  options?: $Shape<{...ApiOptions}>,
};

type QueryResult<TData> = {
  data: ?TData,
  error: ?Error,
  isLoading: boolean,
  isError: boolean,
  isFetching: boolean,
  refetch: () => Promise<void>,
  remove: () => void,
};

export function useApiQuery<Response>(
  url: ApiUrl,
  {
    queryOptions = {},
    apiOptions = {},
  }: {
    queryOptions?: {[string]: mixed},
    apiOptions?: BaseApiRequestOptions,
  },
): QueryResult<Response> {
  const dispatch = useDispatch();

  // Extract API related options
  const {signal, options = {}} = apiOptions;
  const {enabled, ...restOptions} = queryOptions;

  const {pathname, query} = normalizeUrl(url);
  if (enabled && !pathname) {
    throw new Error('Pathname is required');
  }
  // Create query key based on URL and query params
  const queryKey = useMemo(
    () => [pathname, query].filter(Boolean),
    [pathname, JSON.stringify(query)],
  );

  // Define fetch function for React Query
  const fetchData = React.useCallback(async () => {
    if (!pathname) {
      throw new Error('Pathname is required');
    }
    try {
      const response = await dispatch(
        baseRequest(pathname, {
          method: 'GET',
          query: query || undefined,
          options: {
            ...options,
          },
        }),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }, [url, query, options]);

  // Use React Query's useQuery with combined options
  return useQuery(queryKey, fetchData, {
    enabled: Boolean(url),
    ...queryOptions,
  });
}
